import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
// material
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { flexRender, getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual"
import { Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Popover, CircularProgress, TableFooter, Tooltip } from "@mui/material";
// components
import CommonGridToolbar from './commongridtoolbar';
import { Filter, Format } from "./gridhelperfunctions";
import AppCache from '../../services/appcache';
import { fSizeNew1 } from '../../utils/formatNumber';
// ----------------------------------------------------------------------------------------------------------------

ExtendedCommonDataGrid.propTypes = {
    rowData: PropTypes.array,
    columnData: PropTypes.array,
    actionColumn: PropTypes.object,
    loading: PropTypes.bool,
    ClearFilters: PropTypes.func,
    removeFilter: PropTypes.bool,
    setRemoveFilter: PropTypes.func,
    setClickedRowId: PropTypes.func,
    clickedRowId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    globalFilter: PropTypes.string,
    setGlobalFilter: PropTypes.func,
    setLoading: PropTypes.func,
    heightP: PropTypes.string,
    clear: PropTypes.bool,
    IsCheckBox: PropTypes.bool,
    setSelectedRows: PropTypes.func,
    selectedRows: PropTypes.array,
    isPending: PropTypes.bool,
    apiGetCall: PropTypes.bool,
    setApiGetCall: PropTypes.func,
    setNumber: PropTypes.func,
    allSelection: PropTypes.bool,
    selectedDate: PropTypes.object,
    handleDateChange: PropTypes.func,
    handleRegion: PropTypes.func,
    region: PropTypes.array,
    regionData: PropTypes.array,
    handleFranchise: PropTypes.func,
    franchise: PropTypes.string,
    franchiseData: PropTypes.array,
    open: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    setRegion: PropTypes.func,
    buttonsVisibility: PropTypes.object,
    handleApplyRequestFilter: PropTypes.func,
    handleApplyReset: PropTypes.func,
    records: PropTypes.string,
    lastUpdate: PropTypes.string,
    rowIndex: PropTypes.array,
    selectedForDelete: PropTypes.string,
    handelSelect: PropTypes.func,
    masterName: PropTypes.string,
};
// ----------------------------------------------------------------------------------------------------------------

export default function ExtendedCommonDataGrid({ rowData, columnData, actionColumn, loading, ClearFilters,
    removeFilter, setRemoveFilter, setClickedRowId, clickedRowId, globalFilter, setGlobalFilter, setLoading,
    heightP, clear, IsCheckBox, allSelection, setSelectedRows, selectedRows, isPending,
    apiGetCall, setApiGetCall, setNumber, selectedDate, handleDateChange, regionData, region, handleRegion,
    franchiseData, franchise, handleFranchise, setRegion, open, onOpen, onClose, buttonsVisibility,
    handleApplyRequestFilter, handleApplyReset, records, lastUpdate, rowIndex, selectedForDelete,
    handelSelect, masterName }) {

    // const rerender = useReducer(() => ({}), {})[1];

    const myFilterFn = (row, columnId, valueSet) => {
        const rowValueRaw = row.getValue(columnId);
        const rowValue = rowValueRaw?.toString();
        const { formatVariant } = table.getColumn(columnId)?.columnDef.meta ?? { formatVariant: 'String' };

        if (!(valueSet instanceof Set) || valueSet.size === 0) {
            return true; // No filter applied
        }

        // Special case: handle (Blank)
        const hasBlank = valueSet.has('(Blank)');
        if (hasBlank && (rowValueRaw === undefined || rowValueRaw === null || rowValueRaw.toString().trim() === '')) {
            return true;
        }

        switch (formatVariant) {
            case 'string': {
                const normalizedRowValue = rowValue?.trim()?.toUpperCase();
                const normalizedSet = new Set(
                    Array.from(valueSet).map(item =>
                        typeof item === 'string' ? item.trim().toUpperCase() : item
                    )
                );
                return normalizedSet.has(normalizedRowValue);
            }
            case 'monthdate': {
                if (!rowValueRaw) return false;

                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                const parseMonthDateSafe = (value) => {
                    if (!value) return null;

                    // Date object
                    if (value instanceof Date && !Number.isNaN(value.getTime())) {
                        return value;
                    }

                    // ISO / timestamp
                    const d = new Date(value);
                    if (!Number.isNaN(d.getTime())) return d;

                    // MMM-YYYY (Jan-2025)
                    const match = String(value).match(/^([A-Za-z]{3})-(\d{4})$/);
                    if (!match) return null;

                    const [, mon, yyyy] = match;
                    const monthIndex = monthNames.indexOf(mon);
                    if (monthIndex === -1) return null;

                    return new Date(Number(yyyy), monthIndex, 1);
                };

                const formatMonthDate = (date) => {
                    const month = monthNames[date.getMonth()];
                    const year = date.getFullYear();
                    return `${month}-${year}`;
                };

                const rowDate = parseMonthDateSafe(rowValueRaw);
                if (!rowDate) return false;

                const normalizedRowValue = formatMonthDate(rowDate).toLowerCase();

                return Array.from(valueSet).some(filterVal => {
                    const filterDate = parseMonthDateSafe(filterVal);
                    if (!filterDate) return false;

                    return formatMonthDate(filterDate).toLowerCase() === normalizedRowValue;
                });
            }
            case 'datetime': {
                if (!rowValueRaw) return false;

                const monthMap = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                const parseDateTimeSafe = (value) => {
                    if (!value) return null;

                    // Date object
                    if (value instanceof Date && !Number.isNaN(value.getTime())) {
                        return value;
                    }

                    // ISO / timestamp
                    const d = new Date(value);
                    if (!Number.isNaN(d.getTime())) {
                        return d;
                    }

                    // DD-MMM-YYYY HH:mm:ss
                    const match = String(value).match(
                        /^(\d{2})-([A-Za-z]{3})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/
                    );

                    if (!match) return null;

                    const [, dd, mon, yyyy, hh, mm, ss] = match;
                    return new Date(yyyy, monthMap[mon], dd, hh, mm, ss);
                };

                const formatDateTime = (date) => {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = monthNames[date.getMonth()];
                    const year = date.getFullYear();
                    const hh = String(date.getHours()).padStart(2, '0');
                    const mm = String(date.getMinutes()).padStart(2, '0');
                    const ss = String(date.getSeconds()).padStart(2, '0');
                    return `${day}-${month}-${year} ${hh}:${mm}:${ss}`;
                };

                const rowDateTime = parseDateTimeSafe(rowValueRaw);
                if (!rowDateTime) return false;

                const normalizedRowValue = formatDateTime(rowDateTime).toLowerCase();

                return Array.from(valueSet).some(filterVal => {
                    const filterDateTime = parseDateTimeSafe(filterVal);
                    if (!filterDateTime) return false;

                    return formatDateTime(filterDateTime).toLowerCase() === normalizedRowValue;
                });
            }
            case 'date': {
                if (!rowValueRaw) return false;

                const monthMap = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                const parseDateSafe = (value) => {
                    if (!value) return null;

                    // ISO / timestamp
                    const d = new Date(value);
                    if (!Number.isNaN(d.getTime())) return d;

                    // DD-MMM-YYYY
                    const match = String(value).match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
                    if (!match) return null;

                    const [, dd, mon, yyyy] = match;
                    return new Date(yyyy, monthMap[mon], dd);
                };

                const rowDate = parseDateSafe(rowValueRaw);
                if (!rowDate) return false;

                const formatDate = (date) => {
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = monthNames[date.getMonth()];
                    const year = date.getFullYear();
                    return `${day}-${month}-${year}`;
                };

                const rowFormatted = formatDate(rowDate);

                return Array.from(valueSet).some(filterVal => {
                    const filterStr = String(filterVal).trim();

                    return rowFormatted.toLowerCase() === filterStr.toLowerCase();
                });
            }
            case 'numeric': {
                if (rowValueRaw == null || rowValueRaw === '') return false;
                const normalizedRowValue = String(rowValueRaw).trim();
                return Array.from(valueSet).some(val => String(val).trim() === normalizedRowValue);
            }
            case 'decimalnumber': {
                const normalizedRowValue = Number(rowValueRaw).toFixed(2);
                return Array.from(valueSet).some(val => {
                    const searchValue = Number(String(val).replace(/,/g, '')).toFixed(2);
                    return normalizedRowValue === searchValue;
                });
            }
            case 'percentage': {
                if (rowValueRaw == null || rowValueRaw === '') return false;

                const parsePercentage = (val) => {
                    if (typeof val === 'string') val = val.replace('%', '').trim();
                    const num = Number(val);
                    return num <= 1 ? (num * 100).toFixed(2) : num.toFixed(2);
                };

                const normalizedRowValue = parsePercentage(rowValueRaw);
                return Array.from(valueSet).some(val => normalizedRowValue === parsePercentage(val));
            }
            case 'integer': {
                if (rowValueRaw == null || rowValueRaw === '') return false;

                const normalizedRowValue = parseInt(String(rowValueRaw).replace(/,/g, ''), 10);
                return Array.from(valueSet).some(val => {
                    const searchValue = parseInt(String(val).replace(/,/g, ''), 10);
                    return normalizedRowValue === searchValue;
                });
            }
            default:
                return valueSet.has(rowValue);
        }
    };

    const [columnFilters, setColumnFilters] = useState([]);
    const [pageSize, setPageSize] = useState(50);
    const [pageIndex, setPageIndex] = useState(0);
    const [rowSelection, setRowSelection] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);
    const [openFilterId, setOpenFilterId] = useState(null);
    const [openSettingPopup, setOpenSettingPopup] = useState(false);

    const tableContainerRef = useRef(null);

    useEffect(() => {
        clearAllFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clear]);

    useEffect(() => {
        setColumnFilters([]);
        setGlobalFilter('');
    }, [masterName, setGlobalFilter]);

    columnData?.forEach(columnDef => {
        columnDef.textAlign = "center";
    });

    const handleAllChecked = (updater) => {
        const newRowSelection = updater(rowSelection);
        setRowSelection(newRowSelection);
        const selectedIds = Object.keys(newRowSelection).filter((id) => newRowSelection[id]);
        const selectedObjects = totalRowsData.filter(row => selectedIds.includes(row.id.toString()));
        setSelectedRows(selectedObjects);
    }

    // Define columns with checkbox
    const columns = useMemo(
        () => [
            ...(actionColumn ? [actionColumn] : []),
            ...(IsCheckBox
                ? [
                    {
                        id: "select",
                        header: ({ table }) => (
                            <Checkbox
                                checked={table.getIsAllRowsSelected()}
                                indeterminate={table.getIsSomeRowsSelected()}
                                onChange={(e) => {
                                    const checked = e.target.checked;

                                    // Toggle selection for all rows (per-page behavior)
                                    table.toggleAllRowsSelected(checked);

                                    // If handleSelect is provided
                                    if (handelSelect) {
                                        queueMicrotask(() => {
                                            if (checked) {
                                                const allRows = table.getPrePaginationRowModel().rows;
                                                handelSelect(allRows);
                                            } else {
                                                handelSelect([]);
                                            }
                                        });
                                    }
                                }}
                                style={{
                                    height: '20px',
                                    transform: 'scale(0.8)',
                                    width: '30px',
                                }}
                            />
                        ),
                        cell: ({ row, table }) => (
                            <Checkbox
                                checked={row.getIsSelected()}
                                onChange={(e) => {
                                    row.toggleSelected(e.target.checked);

                                    if (handelSelect) {
                                        queueMicrotask(() => {
                                            const selected = table.getSelectedRowModel().rows;
                                            handelSelect(selected);
                                        });
                                    }
                                }}
                                style={{
                                    height: '20px',
                                    transform: 'scale(0.8)',
                                    width: '30px',
                                }}
                            />
                        ),
                        size: 30,
                        textAlign: "center",
                        enableRowSelection: true,
                        enableColumnFilter: false,
                        enableSorting: false,
                        rowProps: {
                            style: {
                                height: '20px',
                            }
                        },
                    }
                ]
                : []),
            ...(columnData.filter(values => values.showInGrid))
        ],
        [actionColumn, columnData, IsCheckBox, handelSelect]
    );

    const table = useReactTable({
        data: rowData,
        columns,
        filterFns: { myFilterFn },
        getRowId: (row) => row.id,

        state: {
            rowSelection, columnFilters, globalFilter,
            pagination: { pageIndex, pageSize },
            columnPinning: { left: actionColumn ? ["actions", "select"] : ["select"], },
        },

        enableSorting: false,
        onRowSelectionChange: handleAllChecked,

        getColumnCanGlobalFilter: () => true,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        globalFilterFn: (row, columnId, filterValue) => {
            if (columnId === 'filterStatus') return true;

            const { formatVariant } = table.getColumn(columnId)?.columnDef.meta ?? { formatVariant: 'String' };
            const rowValue = row.getValue(columnId);
            setPageIndex(0);
            switch (formatVariant) {
                case 'string': {
                    const search = filterValue?.toLowerCase();
                    if (typeof rowValue === 'number') {
                        return String(rowValue).includes(search);
                    }
                    return rowValue?.toLowerCase().includes(search);
                }
                case 'monthdate': {
                    if (!rowValue) return false;

                    const date = new Date(rowValue);
                    if (Number.isNaN(date.getTime())) return false;

                    const month = date.toLocaleString("en-US", { month: "short" });
                    const year = date.getFullYear();

                    const formattedMonth = `${month}-${year}`;  // Display format: MMM-YYYY

                    const filter = filterValue?.trim();
                    if (!filter) return true;

                    return formattedMonth.toLowerCase().startsWith(filter.toLowerCase());
                }
                case 'date': {
                    const rowDate = new Date(rowValue);

                    // ---- Format date part ----
                    const day = String(rowDate.getDate()).padStart(2, "0");
                    const month = rowDate.toLocaleString("en-US", { month: "short" });
                    const year = rowDate.getFullYear();

                    // ---- Build formatted strings ----
                    const formattedDate = `${day}-${month}-${year}`;

                    // ---- Trim filter input ----
                    const filter = filterValue?.trim();

                    // ---- If no filter → show all rows ----
                    if (!filter) {
                        return true;
                    }

                    return formattedDate.startsWith(filter);
                }
                case 'datetime': {
                    if (!rowValue) return false;

                    const date = new Date(rowValue);
                    if (Number.isNaN(date.getTime())) return false;

                    const pad = (n) => String(n).padStart(2, '0');

                    const day = pad(date.getDate());
                    const month = date.toLocaleString("en-US", { month: "short" });
                    const year = date.getFullYear();

                    const hours = pad(date.getHours());
                    const minutes = pad(date.getMinutes());
                    const seconds = pad(date.getSeconds());

                    const formattedDateTime = `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;

                    const filter = filterValue?.trim();
                    if (!filter) return true;

                    return formattedDateTime.toLowerCase().startsWith(filter.toLowerCase());
                }
                case 'integer': {
                    if (!filterValue) return true;

                    const rowNum = Number(String(rowValue).replace(/,/g, ''));
                    const filterNum = String(filterValue).replace(/,/g, '');

                    if (Number.isNaN(rowNum)) return false;

                    return String(rowNum).includes(filterNum);
                }
                case 'percentage': {
                    if (!filterValue) return true;

                    // Remove % and commas
                    const rowRaw = String(rowValue ?? '').replace(/[% ,]/g, '');
                    const filterRaw = String(filterValue ?? '').replace(/[% ,]/g, '');

                    if (!rowRaw || !filterRaw) return false;

                    const rowNum = Number(rowRaw);
                    const filterNum = Number(filterRaw);

                    if (Number.isNaN(rowNum) || Number.isNaN(filterNum)) return false;

                    // Keep percentage values (NOT decimal conversion)
                    const rowFormatted = rowNum.toFixed(2);     // "12.34"
                    const filterFormatted = filterNum.toString(); // "12" or "12.3" or "12.34"

                    // Partial match from start
                    return rowFormatted.startsWith(filterFormatted);
                }
                case 'numeric': {
                    filterValue = filterValue.replace(/,/g, '');
                    return String(Number(rowValue).toFixed(2)).includes(filterValue);
                }
                case 'decimalnumber': {
                    if (!filterValue) return true;

                    const rowValueRaw = String(rowValue || '').replace(/,/g, '');
                    const filterValueRaw = String(filterValue || '').replace(/,/g, '');

                    // normalize to numbers
                    const rowNum = Number(rowValueRaw);
                    const filterNum = Number(filterValueRaw);

                    if (Number.isNaN(rowNum) || Number.isNaN(filterNum)) return false;

                    // convert to same decimal format
                    const normalizedRowValue = rowNum.toFixed(2);     // ex: "8085.00"
                    const normalizedFilterValue = filterNum.toFixed(2); // ex: "808.00"

                    // partial match (THIS is what makes 8,08 work)
                    return normalizedRowValue.includes(normalizedFilterValue.replace('.00', ''));
                }
                default: {
                    const search = filterValue?.toLowerCase();
                    const value = String(rowValue);
                    return value?.toLowerCase().includes(search);
                }
            }
        },

        onStateChange: ({ stateValue }) => {
            if (isPending) return
            // setRowSelection({})
            if (apiGetCall) {
                setSelectedRows([]);
                setApiGetCall(false)
                setLoading(false);
            }
            if (removeFilter) {
                clearAllFilter()
                setRemoveFilter(false)
                setRowSelection({})
            }
            //  setRowSelection({})
        },

        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),

        debugTable: true,
    });

    const totalRowsData = table.getFilteredRowModel().rows;
    const totalRows = table.getFilteredRowModel().rows.length;

    useEffect(() => {
        if (allSelection && totalRowsData.length > 0) {
            const allRowSelection = {};
            totalRowsData?.forEach((row) => {
                allRowSelection[row.id] = true;
            });
            setRowSelection(allRowSelection);
            setSelectedRows(totalRowsData);
        }
        else {
            setRowSelection({});
            setSelectedRows([]);
        }
    }, [allSelection, totalRowsData, setSelectedRows]);

    const clearAllFilter = () => {
        columnData?.forEach(column => {
            // Only proceed if column.showInGrid is falsy
            if (column.showInGrid) {
                const columnId = column.id || column.accessorKey;
                if (columnId) {
                    table.getColumn(columnId)?.setFilterValue('');
                } else {
                    console.warn('Column missing id/accessorKey:', column);
                }
            }
        });
    };

    const handleSetting = () => {
        setOpenSettingPopup(true);
    }

    const confirmSetting = () => {
        setOpenSettingPopup(false);
    }

    const { rows } = table.getRowModel()

    const virtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => pageSize, // Decrease row height (adjust the number)
        overscan: 500,
        onChange: (state) => {
            // setLoading(state.isScrolling);
        },
    });

    const renderHeader = (header) => {
        const handleFilterClick = (event) => {
            if (loading) return;
            setAnchorEl(event.currentTarget);
            setOpenFilterId(header.column.id);
        };

        const handleFilterClose = () => {
            setAnchorEl(null);
            setOpenFilterId(null);
        };

        // Check if the column has an active filter
        const hasFilter = header.column.getFilterValue()?.size > 0;
        const alignMap = {
            right: "flex-end",
            center: "center",
        };

        const justifyContent = alignMap[header.column.columnDef.textAlign] || "flex-start";

        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                <div
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleFilterClick(e)}
                    onClick={handleFilterClick}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        backgroundColor: hasFilter ? 'red' : '',
                        justifyContent,
                        cursor: "pointer",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && header.column.getToggleSortingHandler()(e)}
                        className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flexShrink: 1,
                        }}
                    >
                        <Tooltip title={header.column.columnDef?.meta?.description}>
                            <span
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: "100%",
                                }}
                            >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                        </Tooltip>
                        {{
                            asc: <ArrowDropDown fontSize="small" />,
                            desc: <ArrowDropDown fontSize="small" style={{ transform: "rotate(180deg)" }} />,
                        }[header.column.getIsSorted()] ?? null}

                        {hasFilter && <FilterAltIcon fontSize="small" className="filterIconStyle" />}
                    </div>

                    {header.column.getCanFilter() && (
                        <span
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleFilterClick(e)}
                            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", }}
                            onClick={(e) => { e.stopPropagation(); handleFilterClick(e); }}
                        >
                            <ArrowDropDown fontSize="small" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                        </span>
                    )}
                </div>

                {header.column.getCanFilter() && (
                    <Popover
                        open={openFilterId === header.column.id}
                        anchorEl={anchorEl}
                        onClose={handleFilterClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center", }}
                        transformOrigin={{ vertical: "top", horizontal: "center", }}
                        sx={{
                            '& .MuiPaper-root': {
                                borderRadius: 0,
                                backgroundColor: 'white',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <div style={{ minHeight: '250px', minWidth: "200px", backgroundColor: 'transparent', }}>
                            <Filter column={header.column} handleFilterClose={handleFilterClose} myFilterFn={myFilterFn} setLoading={setLoading} setSelectedRows={setSelectedRows} />
                        </div>
                    </Popover>
                )}
            </div>
        );
    };

    const renderHeaderCell = (header) => (
        <TableCell
            key={header.id}
            className="tanHeadStyle"
            style={{
                width: header.column.columnDef.size,
                maxWidth: header.column.columnDef.size,
                minWidth: header.column.columnDef.size,
                border: "1px solid #ccc",
                textAlign: header.column.columnDef.textAlign,
                position: header.column.getIsPinned() ? 'sticky' : 'relative',
                left: header.column.getIsPinned() === 'left' ? 0 : undefined,
                zIndex: header.column.getIsPinned() ? 10 : undefined,
            }}
            data-pin-left={header.column.getIsPinned() === 'left' ? true : undefined}
        >
            <span className="header-text">
                {!header.isPlaceholder && renderHeader(header)}
            </span>
        </TableCell>
    );

    return (
        <>
            <CommonGridToolbar
                records={records}
                lastUpdate={lastUpdate}
                rowData={totalRowsData.map(item => item.original)}
                columnData={columnData}
                loading={loading}
                setLoading={setLoading}
                ClearFilters={ClearFilters}
                setRemoveFilter={setRemoveFilter}
                setClickedRowId={setClickedRowId}
                clickedRowId={clickedRowId}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                openSettingPopup={openSettingPopup}
                handleSetting={handleSetting}
                confirmSetting={confirmSetting}
                setNumber={setNumber}
                totalRows={totalRows}
                handleDateChange={handleDateChange}
                selectedDate={selectedDate}
                franchiseData={franchiseData}
                franchise={franchise}
                handleFranchise={handleFranchise}
                regionData={regionData}
                region={region}
                handleRegion={handleRegion}
                setRegion={setRegion}
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                buttonsVisibility={buttonsVisibility}
                handleApplyReset={handleApplyReset}
                handleApplyRequestFilter={handleApplyRequestFilter}
                rowIndex={rowIndex}
                selectedForDelete={selectedForDelete}
                masterName={masterName}
            />

            <>
                <TableContainer
                    ref={tableContainerRef}
                    component={Paper}
                    sx={{
                        height: heightP,
                        maxHeight: { lg: 600, xl: 700, xxl: 800 },
                        minHeight: 200,
                        overflowY: rowData.length === 0 ? "hidden" : "auto",
                        border: "1px solid #ccc",
                        position: "relative",
                    }}
                >
                    <style> {` .MuiTableCell-root { padding: 0; } `} </style>

                    <Table stickyHeader style={{ width: "100%", tableLayout: "fixed" }}>
                        <TableHead className="tanHeadStyle">
                            <TableRow >
                                {table.getHeaderGroups().map((headerGroup) =>
                                    headerGroup.headers.map(renderHeaderCell),
                                )}
                            </TableRow>
                        </TableHead>

                        <TableBody className="tableBodyStyle">
                            {loading && (
                                <Box
                                    sx={{
                                        position: "fixed",
                                        top: 60,
                                        bottom: 10,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                                        zIndex: 100,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}

                            {/* Check if there are rows, if none, display 'No rows found' */}
                            {table.getRowModel().rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={Math.max(1, table.getVisibleLeafColumns().length)}
                                        sx={{ padding: 0, borderBottom: 'none' }}>
                                        <Box
                                            sx={{
                                                height: 'calc(100vh - 360px)',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'flex-start',
                                                pt: `calc(${heightP} / 2.2)`,
                                                ml: { xs: '180px', sm: '300px', md: '450px', xl: '550px' },
                                                fontSize: 14,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            No rows found
                                        </Box>

                                    </TableCell>
                                </TableRow>
                            ) : (
                                virtualizer.getVirtualItems().map((virtualRow) => {
                                    const row = rows[virtualRow.index];
                                    let rowClass = '';
                                    const isSelected = selectedRows?.some(selectedRow => selectedRow.id === row.id);
                                    if (isSelected && clickedRowId !== row.id) {
                                        rowClass += ' selectedRow';
                                    }
                                    if (row.original?.newParty?.toLowerCase() === 'yes' && clickedRowId !== row.id) {
                                        rowClass = 'selectedRow';
                                    }
                                    if (
                                        row.original?.imported?.toLowerCase() === 'no' &&
                                        row.original?.newParty?.toLowerCase() !== 'yes'
                                    ) {
                                        rowClass = 'importedRow';
                                    }

                                    return (
                                        <TableRow
                                            key={row.id}
                                            onClick={() => setClickedRowId(row.id)}
                                            className={`${rowClass} ${clickedRowId === row.id ? 'highlightedRow' : ''}`}
                                        >
                                            {row.getVisibleCells().map(cell => (
                                                <Format key={cell.id} cell={cell} />
                                            ))}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                        {/* Sticky footer */}
                        {/*   <TableFooter>
                            <TableRow style={{ backgroundColor: '#FFEFEF', position: 'sticky', bottom: 0 }}>
                                {table.getVisibleLeafColumns().map(column => {
                                    if (!column.columnDef?.isSum) {
                                        return (
                                            <TableCell key={column.id} style={{ border: '1px solid #ccc', padding: '3px', textAlign: column.columnDef.textAlign }}>
                                                -
                                            </TableCell>
                                        );
                                    }
                                    const filteredRows = table.getFilteredRowModel().rows;
                                    const sum = filteredRows.reduce((acc, row) => {
                                        const cellValue = row?.original ? row?.original[column?.id] : row[column.id];
                                        let numericValue = parseFloat(cellValue) || 0;
                                        const { formatVariant } = column.columnDef.meta || { formatVariant: 'string' };

                                        if (formatVariant === 'numeric') {
                                            numericValue = Number(numericValue.toFixed(2)); // convert back to number
                                        }
                                        return acc + numericValue;
                                    }, 0);
                                    return (
                                        <TableCell key={column.id} style={{ border: '1px solid #ccc', padding: '3px', marginTop: '100%', textAlign: column.columnDef.textAlign }}>
                                            {sum !== 0 ? fSizeNew1(sum) : '0.00'}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableFooter>  */}
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[50, 100, 200, 500]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(event, newPage) => setPageIndex(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPageSize(Number.parseInt(event.target.value, 10))
                        setPageIndex(0)
                    }}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            maxHeight: "30px",
                        },
                        ".MuiTablePagination-select, .MuiTablePagination-selectLabel, .MuiTablePagination-actions, .MuiTablePagination-input": {
                            fontSize: "0.7rem",
                        },
                        ".MuiTablePagination-selectLabel": {
                            fontSize: "0.7rem",
                        },
                        ".MuiTablePagination-select": {
                            fontSize: "0.7rem",
                        },
                        ".MuiTablePagination-displayedRows": {
                            fontSize: "0.7rem",
                        },
                    }}
                />
            </>

        </>
    );
}